import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { updateMe, changePassword, uploadMyAvatar, deleteMyAvatar, mfaStatus, mfaRegenerateBackupCodes, type MfaStatus } from '../api/auth';
import {
  createAdminUser,
  deleteAdminUser,
  getAdminUserAccess,
  listAdminSpaces,
  listAdminUsers,
  listModuleCatalog,
  resetAdminUserMfa,
  resetAdminUserPassword,
  saveAdminUserAccess,
  validateMm,
  type AccessLevel,
  type AdminSpace,
  type AdminUser,
  type MmValidateResult,
  type SpaceModuleCatalog,
  type UserRole,
} from '../api/admin';
import { ApiError, api } from '../api/client';
import { useFeather } from '../shell/useFeather';
import { MfaStepUpModal } from '../shell/MfaStepUpModal';
import { MobileDevicesPane } from './MobileDevicesPane';
import { ApiAccessPane, UserApiAccessSection } from './ApiAccess';
import { MarketingMailingSettings, type MailingTab } from './MarketingMailingSettings';
import { AffilmasterPartnersSettings } from './AffilmasterPartnersSettings';
import { VipPartnersSettings } from './VipPartnersSettings';
import { ShortenerSettings } from './ShortenerSettings';
import { LandingSettings } from './LandingSettings';
import { HelpdeskSettings, type TabKey as HelpdeskTab } from '../helpdesk/settings/HelpdeskSettings';
import { CrmServisniSablonyPage } from '../crm/CrmServisniSablonyPage';
import { NotificationsSettings } from './NotificationsSettings';
import { MailingSettingsPanel } from '../mailing/MailingSettingsPanel';
import { ActivityPane } from './ActivityPane';
import './settings-redesign.css';
import { confirmDialog } from '../shell/dialogs';

// Deep-link z Kanálů: /nastaveni?mkt=<tab> otevře Nastavení→Marketing na tom tabu.
const MKT_TABS: MailingTab[] = ['senders', 'mail', 'modal', 'partners', 'segments'];
function initialMktTab(): MailingTab | null {
  const v = new URLSearchParams(window.location.search).get('mkt');
  return v && (MKT_TABS as string[]).includes(v) ? (v as MailingTab) : null;
}

const ADMIN_PERM = 'admin:users:manage';

type View = 'aktivita' | 'api' | 'p1-servis' | 'overview' | 'notifications' | 'users' | 'profile' | 'password' | 'mfa' | 'zarizeni' | 'mkt-mailing' | 'shortener' | 'landing' | 'helpdesk' | 'af-klienti' | 'am-vip' | 'ap-servis-sablony' | 'am-mailing' | 'vystupy';

interface SectionLink {
  label: string;
  view?: View;
  hdTab?: string;
  mailingTab?: string;
  soon?: boolean;
}
interface Section {
  title: string;
  icon: string;
  desc?: string;
  highlight?: boolean;
  links: SectionLink[];
}

function SettingsDetailHeader({
  eyebrow,
  title,
  description,
  onBack,
}: {
  eyebrow: string;
  title: string;
  description: string;
  onBack: () => void;
}) {
  return (
    <header className="settings-detail-head">
      <button type="button" className="settings-back" onClick={onBack}>
        Nastavení
      </button>
      <div className="settings-eyebrow">{eyebrow}</div>
      <h1>{title}</h1>
      <p>{description}</p>
    </header>
  );
}

const StrankyNastaveniPage = lazy(() => import('../affilmaster/stranky/StrankyNastaveniPage').then((m) => ({ default: m.StrankyNastaveniPage })));

export function SettingsPage() {
  const { me } = useAuth();
  const isAdmin = !!me?.permissionKeys?.includes(ADMIN_PERM);
  const isSuper = !!me?.canAccessAllSpacesInTenant || !!me?.roleKeys?.includes('tenant_admin');
  const [view, setView] = useState<View>(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('view');
    if (initialMktTab()) return 'mkt-mailing';
    if (params.get('mailing')) return 'am-mailing';
    if (requested === 'vystupy' || requested === 'shortener' || requested === 'landing') return requested;
    return 'overview';
  });
  const [helpdeskTab, setHelpdeskTab] = useState<HelpdeskTab>('schranky');
  useFeather([view]);
  useEffect(() => { window.scrollTo(0, 0); }, [view]);

  const spaceLabel = me?.activeSpaceSlug
    ? me.activeSpaceSlug.charAt(0).toUpperCase() + me.activeSpaceSlug.slice(1)
    : 'Rondo';
  // Marketing sub-stránky (mailing/shortener/landing) jsou zatím postavené jen
  // pro Rondo (API je Rondo-scoped). V jiných Space je sekce „připravujeme".
  const isRondo = me?.activeSpaceSlug === 'rondo' || me?.activeSpaceSlug == null;
  const isAffilmaster = me?.activeSpaceSlug === 'affilmaster';
  const isAffilplatform = me?.activeSpaceSlug === 'affilplatform';
  const canManageServis = !!me?.permissionKeys?.includes('crm:manage');
  const canManageStranky = (isAffilmaster || isAffilplatform || me?.activeSpaceSlug == null) && (isAdmin || isSuper || !!me?.permissionKeys?.includes('vystupy:manage'));

  const sections: Section[] = [
    {
      title: 'Můj účet',
      icon: 'user',
      desc: isAdmin ? 'Profil, heslo a správa uživatelů.' : 'Profil, heslo.',
      highlight: true,
      links: [
        { label: 'Profil', view: 'profile' },
        { label: 'Změna hesla', view: 'password' },
        { label: 'Dvoufázové ověření', view: 'mfa' },
        { label: 'Přihlášená zařízení', view: 'zarizeni' },
        { label: 'API přístup', view: 'api' },
        ...(isAdmin ? [{ label: 'Uživatelé', view: 'users' as View }] : []),
        ...(isAdmin ? [{ label: 'Servisní e-maily účtu', view: 'p1-servis' as View }] : []),
        ...(isSuper ? [{ label: 'Aktivita uživatelů', view: 'aktivita' as View }] : []),
      ],
    },
    ...(isAdmin
      ? [
          {
            title: 'Notifikace',
            icon: 'bell',
            desc: 'Přehled zpráv, které P1 posílá do Mattermostu, a jejich vypnutí.',
            links: [{ label: 'Mattermost', view: 'notifications' as View }],
          },
        ]
      : []),
    ...((isAffilplatform || isAffilmaster) && canManageServis
      ? [
          {
            title: 'Servisní e-maily',
            icon: 'mail',
            desc: `Šablony transakčních e-mailů (double opt-in, potvrzení) pro ${spaceLabel}.`,
            links: [{ label: 'Šablony', view: 'ap-servis-sablony' as View }],
          },
        ]
      : []),
    ...(canManageStranky
      ? [{
          title: `Výstupy - ${spaceLabel}`,
          icon: 'file-text',
          desc: 'Weby podpory: domény, jazykové mutace, indexace a texty úvodní stránky.',
          links: [{ label: 'Podpora – domény a jazyky', view: 'vystupy' as View }],
        }]
      : []),
    ...(isAdmin
      ? [
          ...(isAffilmaster || isAffilplatform
            ? [{
                title: `Mailing - ${spaceLabel}`,
                icon: 'target',
                desc: 'Odesílatelé, šablony kampaní a stav rozesílky přes SendGrid.',
                links: [
                  { label: 'Odesílatelé', view: 'am-mailing' as View, mailingTab: 'senders' },
                  { label: 'Šablony kampaní', view: 'am-mailing' as View, mailingTab: 'templates' },
                  { label: 'Rozesílka', view: 'am-mailing' as View, mailingTab: 'delivery' },
                ],
              }]
            : []),
          ...(!isAffilmaster ? [{
            title: `Marketing - ${spaceLabel}`,
            icon: 'target',
            desc: 'Šablony, odesílatelé, AI agenti a obecné nastavení marketingu (Mailkit, WhatsApp, SMS, automatizace).',
            links: [
              { label: 'Šablony (Maily, LP, Modaly, SMS)', ...(isRondo ? { view: 'mkt-mailing' as View } : { soon: true }) },
              { label: 'Odesílatelé', ...(isRondo ? { view: 'mkt-mailing' as View } : { soon: true }) },
              { label: 'Obecné nastavení', ...(isRondo ? { view: 'mkt-mailing' as View } : { soon: true }) },
              { label: 'Shortener adresy', ...(isRondo ? { view: 'shortener' as View } : { soon: true }) },
              { label: 'Landing domény', ...(isRondo ? { view: 'landing' as View } : { soon: true }) },
              { label: 'AI agenti', soon: true },
            ],
          }] : []),
          {
            title: `Finance a Partneři - ${spaceLabel}`,
            icon: 'briefcase',
            desc: 'Číselník partnerů (leads, CZ/SK/RO) + klienti tržeb (firmy které platí za reklamu - IČ pro UOL).',
            links: [
              { label: 'Číselník partnerů', soon: true },
              { label: 'Klienti tržeb', ...(isAffilmaster ? { view: 'af-klienti' as View } : { soon: true }) },
              ...(isAffilmaster ? [{ label: 'VIP affiliate partneři', view: 'am-vip' as View }] : []),
            ],
          },
          {
            title: 'Helpdesk',
            icon: 'life-buoy',
            desc: 'Schránky, šablony emailů, přístup do helpdesku a AI agenti.',
            links: [
              { label: 'Schránky (OAuth)', view: 'helpdesk' as View, hdTab: 'schranky' },
              { label: 'Email šablony', view: 'helpdesk' as View, hdTab: 'sablony' },
              { label: 'Pravidla', view: 'helpdesk' as View, hdTab: 'pravidla' },
              { label: 'AI agenti', view: 'helpdesk' as View, hdTab: 'ai' },
              { label: 'Tagy', view: 'helpdesk' as View, hdTab: 'tagy' },
              { label: 'Přístup uživatelů', view: 'helpdesk' as View, hdTab: 'pristupy' },
            ],
          },
          {
            title: 'Integrace',
            icon: 'zap',
            desc: 'API klíče a webhooky pro napojení externích systémů.',
            links: [
              { label: 'API klíče', soon: true },
              { label: 'Webhooks', soon: true },
            ],
          },
        ]
      : []),
  ];

  const accountSection = sections[0]!;
  const directorySections = sections.slice(1);
  const availableCount = sections.reduce((sum, section) => sum + section.links.filter((link) => !!link.view).length, 0);
  const soonCount = sections.reduce((sum, section) => sum + section.links.filter((link) => link.soon).length, 0);
  const profileName = me?.displayName || [me?.firstName, me?.lastName].filter(Boolean).join(' ') || 'Můj účet';
  const profileInitials = (() => {
    const words = profileName.split(/\s+/).filter(Boolean);
    return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase() || 'P1';
  })();

  function openLink(link: SectionLink) {
    if (!link.view) return;
    if (link.hdTab) setHelpdeskTab(link.hdTab as HelpdeskTab);
    if (link.view === 'shortener' || link.view === 'landing') {
      const url = new URL(window.location.href);
      url.searchParams.set('view', link.view);
      window.history.replaceState({}, '', url.toString());
    }
    if (link.mailingTab) {
      const url = new URL(window.location.href);
      url.searchParams.set('mailing', link.mailingTab);
      window.history.replaceState({}, '', url.toString());
    }
    setView(link.view);
  }

  if (view === 'mkt-mailing') {
    return <MarketingMailingSettings onBack={() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('mkt');
      window.history.replaceState({}, '', url.toString());
      setView('overview');
    }} initialTab={initialMktTab() ?? 'senders'} />;
  }

  if (view === 'am-mailing') {
    const subdomain = window.location.hostname.split('.')[0] === 'affilmaster';
    return (
      <MailingSettingsPanel
        space={isAffilplatform ? 'affilplatform' : 'affilmaster'}
        spaceLabel={spaceLabel}
        countries={['CZ', 'SK']}
        templatesPath={isAffilplatform || subdomain ? '/mailing/sablony' : '/affilmaster/mailing/sablony'}
        onBack={() => {
          const url = new URL(window.location.href);
          url.searchParams.delete('mailing');
          window.history.replaceState({}, '', url.toString());
          setView('overview');
        }}
      />
    );
  }

  if (view === 'vystupy') {
    return (
      <section className="settings-page settings-detail-page">
        <SettingsDetailHeader
          eyebrow="AFFILMASTER VÝSTUPY"
          title="Nastavení Podpory"
          description="Domény, jazykové mutace, indexace a obsah úvodní stránky."
          onBack={() => {
            const url = new URL(window.location.href);
            url.searchParams.delete('view');
            window.history.replaceState({}, '', url.toString());
            setView('overview');
          }}
        />
        <Suspense fallback={<div className="p-4 text-muted">Načítám…</div>}>
          <StrankyNastaveniPage />
        </Suspense>
      </section>
    );
  }

  if (view === 'af-klienti') {
    return <AffilmasterPartnersSettings onBack={() => setView('overview')} />;
  }
  if (view === 'am-vip' && isAffilmaster && isAdmin) {
    return <VipPartnersSettings onBack={() => setView('overview')} />;
  }

  if (view === 'notifications') {
    return (
      <section className="settings-page settings-detail-page">
        <SettingsDetailHeader
          eyebrow="NOTIFIKACE"
          title="Notifikace do Mattermostu"
          description="Které zprávy P1 posílá, komu chodí a co je právě vypnuté."
          onBack={() => setView('overview')}
        />
        <NotificationsSettings />
      </section>
    );
  }

  if (view === 'ap-servis-sablony') {
    return (
      <section className="settings-page settings-detail-page">
        <SettingsDetailHeader
          eyebrow={spaceLabel.toUpperCase()}
          title="Servisní e-maily"
          description="Transakční šablony a jejich obsah pro tento Space."
          onBack={() => setView('overview')}
        />
        <CrmServisniSablonyPage />
      </section>
    );
  }

  if (view === 'helpdesk') {
    return (
      <HelpdeskSettings
        onClose={() => setView('overview')}
        initialTab={helpdeskTab}
        backLabel="Zpět na Nastavení"
      />
    );
  }

  if (view !== 'overview') {
    const detail = view === 'users'
      ? { title: 'Uživatelé', description: 'Přístupy, role a oprávnění napříč Platform 1.' }
      : view === 'profile'
        ? { title: 'Profil', description: 'Osobní údaje, avatar a propojení s Mattermostem.' }
        : view === 'shortener'
          ? { title: 'Shortener adresy', description: 'Domény a pravidla pro zkrácené odkazy.' }
          : view === 'landing'
            ? { title: 'Landing domény', description: 'Domény používané pro kampaně a landing pages.' }
            : view === 'mfa'
              ? { title: 'Dvoufázové ověření', description: 'Druhý faktor pro přihlášení a záložní kódy.' }
              : view === 'zarizeni'
                ? { title: 'Přihlášená zařízení', description: 'Mobilní aplikace přihlášené k vašemu účtu.' }
              : view === 'api'
                ? { title: 'API přístup', description: 'Osobní tokeny pro připojení aplikací k Úkolům.' }
              : view === 'p1-servis'
                ? { title: 'Servisní e-maily účtu', description: 'Náhled e-mailů pro obnovení hesla a notifikace o změně.' }
                : view === 'aktivita'
                  ? { title: 'Aktivita uživatelů', description: 'Kdo kdy četl a upravoval citlivá data (Rondo, Affilmaster, CRM).' }
                : { title: 'Změna hesla', description: 'Aktualizujte přihlašovací heslo ke svému účtu.' };
    return (
      <section className="settings-page settings-detail-page">
        <SettingsDetailHeader
          eyebrow={spaceLabel.toUpperCase()}
          title={detail.title}
          description={detail.description}
          onBack={() => {
            if (view === 'shortener' || view === 'landing') {
              const url = new URL(window.location.href);
              url.searchParams.delete('view');
              window.history.replaceState({}, '', url.toString());
            }
            setView('overview');
          }}
        />
        {view === 'users' && isAdmin && <UsersPane />}
        {view === 'profile' && <ProfilePane />}
        {view === 'password' && <PasswordPane />}
        {view === 'mfa' && <MfaPane />}
        {view === 'zarizeni' && <MobileDevicesPane />}
        {view === 'api' && <ApiAccessPane />}
        {view === 'p1-servis' && isAdmin && <AccountMailsPane />}
        {view === 'aktivita' && isSuper && <ActivityPane />}
        {view === 'shortener' && isAdmin && <ShortenerSettings />}
        {view === 'landing' && isAdmin && <LandingSettings />}
      </section>
    );
  }

  return (
    <section className="settings-page settings-overview-page">
      <header className="settings-hero">
        <div>
          <div className="settings-eyebrow">{spaceLabel.toUpperCase()}</div>
          <h1>Nastavení</h1>
          <p>Účet, tým a provozní nástroje pro {spaceLabel} na jednom místě.</p>
        </div>
        <div className="settings-hero-meta" aria-label="Stav nastavení">
          <span><strong>{availableCount}</strong> dostupných voleb</span>
          {soonCount > 0 && <span><strong>{soonCount}</strong> připravujeme</span>}
        </div>
      </header>

      <div className={`settings-workspace${directorySections.length === 0 ? ' settings-workspace-solo' : ''}`}>
        <aside className="settings-account-panel" aria-labelledby="settings-account-title">
          <div className="settings-account-person">
            <div className="settings-account-avatar" aria-hidden="true">
              {me?.avatarUrl ? <img src={me.avatarUrl} alt="" /> : <span>{profileInitials}</span>}
            </div>
            <div>
              <span>Váš účet</span>
              <h2 id="settings-account-title">{profileName}</h2>
              <p>{me?.email ?? ''}</p>
            </div>
          </div>
          <div className="settings-account-actions">
            {accountSection.links.map((link) => (
              <button type="button" key={link.label} onClick={() => openLink(link)}>
                <span>{link.label}</span>
              </button>
            ))}
          </div>
        </aside>

        {directorySections.length > 0 && (
          <div className="settings-directory">
            {directorySections.map((section) => (
              <section className="settings-directory-section" key={section.title}>
                <header className="settings-directory-head">
                  <span className="settings-directory-icon" aria-hidden="true">
                    <span className="feather-icon"><i data-feather={section.icon}></i></span>
                  </span>
                  <div>
                    <h2>{section.title}</h2>
                    {section.desc && <p>{section.desc}</p>}
                  </div>
                </header>
                <div className="settings-directory-actions">
                  {section.links.map((link) => (
                    <button
                      type="button"
                      className={link.view ? '' : 'is-soon'}
                      key={link.label}
                      disabled={!link.view}
                      onClick={() => openLink(link)}
                    >
                      <span>{link.label}</span>
                      {link.soon ? (
                        <span className="settings-soon">Připravujeme</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProfilePane() {
  const { me, refresh } = useAuth();
  const [firstName, setFirstName] = useState(me?.firstName ?? '');
  const [lastName, setLastName] = useState(me?.lastName ?? '');
  const [mm, setMm] = useState(me?.mattermostUserId ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(me?.avatarUrl ?? null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avInit = ((me?.firstName?.[0] ?? '') + (me?.lastName?.[0] ?? '')).toUpperCase() || (me?.email?.[0] ?? '?').toUpperCase();

  async function onAvatarFile(file: File) {
    setMsg(null); setErr(null);
    if (!file.type.startsWith('image/')) { setErr('Vyber prosím obrázek (PNG, JPG, WebP, GIF).'); return; }
    if (file.size > 1_500_000) { setErr('Obrázek je příliš velký (max 1,5 MB).'); return; }
    setAvatarBusy(true);
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result));
        r.onerror = () => rej(new Error('read'));
        r.readAsDataURL(file);
      });
      const r = await uploadMyAvatar(dataUrl, file.type);
      setAvatarUrl(r.avatarUrl);
      await refresh();
      setMsg('Profilová fotka nahrána.');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Nahrání fotky selhalo.');
    } finally {
      setAvatarBusy(false);
    }
  }

  async function removeAvatar() {
    setMsg(null); setErr(null); setAvatarBusy(true);
    try {
      await deleteMyAvatar();
      setAvatarUrl(null);
      await refresh();
      setMsg('Profilová fotka odebrána.');
    } catch {
      setErr('Odebrání fotky selhalo.');
    } finally {
      setAvatarBusy(false);
    }
  }

  async function save() {
    setSaving(true);
    setMsg(null);
    setErr(null);
    try {
      await updateMe({
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        mattermostUserId: mm.trim() || null,
      });
      await refresh();
      setMsg('Uloženo.');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-form-layout settings-profile-pane">
      <section className="settings-form-panel">
        <header className="settings-panel-head">
          <div>
            <span>Osobní údaje</span>
            <h2>Jak vás ostatní uvidí</h2>
            <p>Tyto informace se zobrazují u úkolů, komentářů a interních aktivit.</p>
          </div>
        </header>
        <div className="settings-form-body">
          {err && <div className="alert alert-danger">{err}</div>}
          {msg && <div className="alert alert-success">{msg}</div>}
          <div className="settings-avatar-row">
            <div className="settings-profile-avatar">
              {avatarUrl ? <img src={avatarUrl} alt="Profilová fotka" /> : <span>{avInit}</span>}
            </div>
            <div className="settings-avatar-copy">
              <strong>Profilová fotografie</strong>
              <p>PNG, JPG, WebP nebo GIF, maximálně 1,5 MB.</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void onAvatarFile(f); e.target.value = ''; }}
              />
              <div className="settings-inline-actions">
                <button type="button" className="btn btn-outline-secondary" disabled={avatarBusy} onClick={() => fileRef.current?.click()}>
                  {avatarBusy ? 'Pracuji…' : avatarUrl ? 'Změnit fotografii' : 'Nahrát fotografii'}
                </button>
                {avatarUrl && (
                  <button type="button" className="btn btn-link settings-danger-link" disabled={avatarBusy} onClick={() => void removeAvatar()}>
                    Odebrat
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="settings-field">
            <label className="form-label">E-mail</label>
            <input className="form-control" value={me?.email ?? ''} disabled />
            <small>Přihlašovací e-mail spravuje administrátor.</small>
          </div>
          <div className="settings-field-grid">
            <div className="settings-field">
              <label className="form-label">Jméno</label>
              <input className="form-control" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="settings-field">
              <label className="form-label">Příjmení</label>
              <input className="form-control" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div className="settings-field">
            <label className="form-label">Mattermost e-mail nebo User ID</label>
            <input className="form-control" placeholder="jmeno@firma.cz" value={mm} onChange={(e) => setMm(e.target.value)} />
            <small>Slouží k identifikaci a doručování interních notifikací.</small>
          </div>
        </div>
        <footer className="settings-form-footer">
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Ukládám…' : 'Uložit změny'}
          </button>
        </footer>
      </section>
      <aside className="settings-context-panel">
        <span>Profil v Platform 1</span>
        <h2>Jedna identita napříč Spaces</h2>
        <p>Jméno, fotografie a Mattermost propojení se používají ve všech pracovních prostorech, ke kterým máte přístup.</p>
      </aside>
    </div>
  );
}

function PasswordPane() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function save() {
    setError(null);
    setDone(false);
    if (next.length < 12) {
      setError('Nové heslo musí mít alespoň 12 znaků.');
      return;
    }
    if (next !== confirm) {
      setError('Nové heslo a potvrzení se neshodují.');
      return;
    }
    setSaving(true);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'INVALID_CURRENT_PASSWORD') setError('Současné heslo není správné.');
        else if (e.code === 'PASSWORD_TOO_SHORT' || e.code === 'PASSWORD_BREACHED' || e.code === 'PASSWORD_CONTAINS_EMAIL' || e.code === 'PASSWORD_TOO_LONG') setError(e.message || 'Heslo nesplňuje pravidla.');
        else setError(`Chyba: ${e.status} ${e.code}`);
      } else {
        setError(String(e));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-form-layout settings-password-pane">
      <section className="settings-form-panel">
        <header className="settings-panel-head">
          <div>
            <span>Zabezpečení</span>
            <h2>Nastavte nové heslo</h2>
            <p>Změna se projeví při příštím přihlášení do Platform 1.</p>
          </div>
        </header>
        <div className="settings-form-body">
          <div className="settings-field">
            <label className="form-label">Současné heslo</label>
            <input
              type="password"
              className="form-control"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <div className="settings-field">
            <label className="form-label">Nové heslo</label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="alespoň 12 znaků"
            />
            <small>Minimálně 12 znaků, ne z uniklých databází.</small>
          </div>
          <div className="settings-field">
            <label className="form-label">Potvrzení nového hesla</label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          {error && <div className="alert alert-danger">{error}</div>}
          {done && <div className="alert alert-success">Heslo bylo změněno.</div>}
        </div>
        <footer className="settings-form-footer">
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !current || !next || !confirm}
            onClick={() => void save()}
          >
            {saving ? 'Ukládám…' : 'Změnit heslo'}
          </button>
        </footer>
      </section>
      <aside className="settings-context-panel">
        <span>Doporučení</span>
        <h2>Silné a unikátní heslo</h2>
        <p>Použijte heslo, které nemáte u jiné služby. Bezpečná kombinace obsahuje delší frázi, čísla a speciální znaky.</p>
      </aside>
    </div>
  );
}

function UsersPane() {
  const { me } = useAuth();
  const isSuper = !!me?.canAccessAllSpacesInTenant || !!me?.roleKeys?.includes('tenant_admin');
  const [stepUpRetry, setStepUpRetry] = useState<(() => Promise<void>) | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [spaces, setSpaces] = useState<AdminSpace[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  useFeather([users?.length ?? 0]);

  async function reload() {
    setError(null);
    try {
      const [u, s] = await Promise.all([listAdminUsers(), listAdminSpaces()]);
      setUsers(u);
      setSpaces(s);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status} ${e.code}` : String(e));
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const spaceName = (id: number) => spaces.find((s) => s.id === id)?.name ?? `#${id}`;

  async function resetMfa(u: AdminUser) {
    const label = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
    if (!(await confirmDialog({ title: 'Zrušit dvoufázové ověření?', message: `Uživatel „${label}“ bude odhlášen a při dalším přihlášení si 2FA nastaví znovu.`, confirmLabel: 'Zrušit 2FA', danger: true }))) return;
    setError(null);
    const run = async () => {
      await resetAdminUserMfa(u.id);
      await reload();
    };
    try {
      await run();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'MFA_STEP_UP_REQUIRED') setStepUpRetry(() => run);
      else setError(e instanceof ApiError ? `Reset 2FA selhal: ${e.code}` : String(e));
    }
  }

  async function removeUser(u: AdminUser) {
    const label = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
    if (!(await confirmDialog({ title: 'Smazat uživatele?', message: `„${label}“ ztratí přístup; úkoly a historie zůstanou zachované.`, confirmLabel: 'Smazat', danger: true }))) return;
    setError(null);
    try {
      await deleteAdminUser(u.id);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? `Smazání selhalo: ${e.code}` : String(e));
    }
  }

  return (
    <section className="settings-users-panel">
      <header className="settings-users-head">
        <div>
          <span>Správa přístupů</span>
          <h2>Tým v Platform 1</h2>
          <p>{users ? `${users.length} uživatelů s přístupem` : 'Načítám uživatele…'}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
          <span className="feather-icon" aria-hidden="true"><i data-feather="plus"></i></span>
          Nový uživatel
        </button>
      </header>
      <div className="settings-users-body">
        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : !users ? (
          <p className="settings-loading">Načítám…</p>
        ) : (
          <div className="settings-users-table-wrap">
            <table className="table align-middle settings-users-table">
              <thead>
                <tr>
                  {['Uživatel', 'Role', 'Spaces', 'Akce'].map((heading) => <th key={heading}>{heading}</th>)}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-muted py-3">Žádní uživatelé.</td></tr>
                ) : (
                  users.map((u) => {
                    const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
                    const initials = ((u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? u.email[0] ?? '')).toUpperCase();
                    const role = roleFromKeys(u.roleKeys);
                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="settings-user-identity">
                            <span aria-hidden="true">{initials}</span>
                            <div><strong>{name}</strong><small>{u.email}</small></div>
                          </div>
                        </td>
                        <td>
                          <span className={`settings-role settings-role-${role}`}>
                            {role === 'super_admin' ? 'Super Admin' : role === 'admin' ? 'Admin projektu' : 'Uživatel'}
                          </span>
                        </td>
                        <td>
                          {u.spaceIds.length === 0 ? (
                            <span className="text-muted">-</span>
                          ) : (
                            u.spaceIds.map((id) => (
                              <span key={id} className="settings-space-chip">{spaceName(id)}</span>
                            ))
                          )}
                        </td>
                        <td className="settings-user-actions">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setEditUser(u)}
                          >
                            Upravit
                          </button>
                          {isSuper && (
                            <button
                              type="button"
                              className="btn btn-link"
                              onClick={() => void resetMfa(u)}
                            >
                              Reset 2FA
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-link settings-danger-link"
                            onClick={() => void removeUser(u)}
                          >
                            Smazat
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {stepUpRetry && (
        <MfaStepUpModal
          title="Reset 2FA - ověření"
          onClose={() => setStepUpRetry(null)}
          onVerified={async () => {
            const run = stepUpRetry;
            setStepUpRetry(null);
            try {
              await run();
            } catch (e) {
              setError(e instanceof ApiError ? `Reset 2FA selhal: ${e.code}` : String(e));
            }
          }}
        />
      )}
      {(modalOpen || editUser) && (
        <NewUserModal
          editUser={editUser}
          onClose={() => {
            setModalOpen(false);
            setEditUser(null);
          }}
          onDone={() => {
            setModalOpen(false);
            setEditUser(null);
            void reload();
          }}
        />
      )}
    </section>
  );
}

// Kryptograficky kvalitní náhoda (crypto.getRandomValues), bez zaměnitelných
// znaků (0/O/1/l/I); garantuje ≥1 velké, malé písmeno, číslici a symbol.
function generatePassword(len = 14): string {
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digit = '23456789';
  const symbol = '!@#$%&*?';
  const all = lower + upper + digit + symbol;
  const rnd = (n: number) => {
    const a = new Uint32Array(1);
    crypto.getRandomValues(a);
    return a[0]! % n;
  };
  const pick = (set: string) => set[rnd(set.length)]!;
  const chars = [pick(lower), pick(upper), pick(digit), pick(symbol)];
  for (let i = chars.length; i < len; i++) chars.push(pick(all));
  // Fisher–Yates shuffle, ať povinné znaky nejsou na fixních pozicích.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rnd(i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join('');
}

type Levels = Record<number, Record<string, AccessLevel | ''>>;

function roleFromKeys(keys: string[] | undefined): UserRole {
  if (keys?.includes('tenant_admin')) return 'super_admin';
  if (keys?.some((k) => k.endsWith('_admin'))) return 'admin';
  return 'user';
}

function NewUserModal({
  editUser,
  onClose,
  onDone,
}: {
  editUser?: AdminUser | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { me } = useAuth();
  const isEdit = !!editUser;
  // Super Admina smí udělit jen Super Admin (1:1 prod - volba se jinak skryje).
  const callerSuper =
    !!me?.roleKeys?.includes('tenant_admin') || !!me?.permissionKeys?.includes('spaces:access:all_in_tenant');
  const [email, setEmail] = useState(editUser?.email ?? '');
  const [firstName, setFirstName] = useState(editUser?.firstName ?? '');
  const [lastName, setLastName] = useState(editUser?.lastName ?? '');
  const [mm, setMm] = useState(editUser?.mattermostUserId ?? '');
  const [mmCheck, setMmCheck] = useState<MmValidateResult | null>(null);
  const [mmChecking, setMmChecking] = useState(false);
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [generated, setGenerated] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<SpaceModuleCatalog[]>([]);
  const [levels, setLevels] = useState<Levels>({});
  const [role, setRole] = useState<UserRole>(roleFromKeys(editUser?.roleKeys));
  const [adminSpaceIds, setAdminSpaceIds] = useState<number[]>(
    roleFromKeys(editUser?.roleKeys) === 'admin' ? (editUser?.spaceIds ?? []) : [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  useEffect(() => {
    listModuleCatalog().then((c) => setCatalog(c.filter((s) => s.modules.length > 0))).catch(() => setCatalog([]));
  }, []);
  useEffect(() => {
    if (!editUser) return;
    getAdminUserAccess(editUser.id)
      .then((acc) => {
        const next: Levels = {};
        for (const [spaceId, mods] of Object.entries(acc)) {
          next[Number(spaceId)] = { ...(mods as Record<string, AccessLevel>) };
        }
        setLevels(next);
      })
      .catch(() => undefined);
  }, [editUser]);
  useFeather([catalog.length]);

  function doGenerate() {
    const pw = generatePassword();
    setPassword(pw);
    setGenerated(pw);
    setShowPwd(true);
    setError(null);
  }

  function setLevel(spaceId: number, key: string, level: AccessLevel | '') {
    setLevels((p) => ({ ...p, [spaceId]: { ...(p[spaceId] ?? {}), [key]: level } }));
  }

  async function checkMm() {
    if (!mm.trim()) return;
    setMmChecking(true);
    setMmCheck(null);
    try {
      setMmCheck(await validateMm(mm.trim()));
    } catch (e) {
      setMmCheck({ ok: false, server: '', error: e instanceof ApiError ? e.message : 'Chyba' });
    } finally {
      setMmChecking(false);
    }
  }

  const [stepUp, setStepUp] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    await submit();
  }

  async function submit() {
    setError(null);
    const pw = password.trim();
    if (pw && pw.length < 12) {
      setError('Heslo musí mít alespoň 12 znaků.');
      return;
    }
    setBusy(true);
    try {
      let userId: number;
      let createdTemp: string | undefined;
      if (isEdit && editUser) {
        userId = editUser.id;
        if (pw) await resetAdminUserPassword(userId, pw);
      } else {
        const created = await createAdminUser({
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          mattermostUserId: mm.trim() || undefined,
          password: pw || undefined,
        });
        userId = created.userId;
        createdTemp = created.tempPassword;
      }
      // Sestav access dle role:
      //  - super_admin → access prázdné (celé P1)
      //  - admin       → vybrané Spacy (prázdné moduly, plný přístup z role)
      //  - user        → matice modulů (read/write)
      let access: { spaceId: number; modules: Record<string, AccessLevel> }[] = [];
      if (role === 'admin') {
        access = adminSpaceIds.map((spaceId) => ({ spaceId, modules: {} }));
      } else if (role === 'user') {
        access = catalog
          .map((sp) => {
            const modules: Record<string, AccessLevel> = {};
            for (const m of sp.modules) {
              const lvl = levels[sp.spaceId]?.[m.key];
              if (lvl === 'read' || lvl === 'write') modules[m.key] = lvl;
            }
            return { spaceId: sp.spaceId, modules };
          })
          .filter((e) => Object.keys(e.modules).length > 0);
      }
      await saveAdminUserAccess(userId, role, access);
      if (createdTemp) setTempPassword(createdTemp);
      else if (pw) setTempPassword(pw);
      else onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'MFA_STEP_UP_REQUIRED') setStepUp(true);
      else setError(e instanceof ApiError ? (e.message && e.code !== `HTTP_${e.status}` ? `${e.code}: ${e.message}` : `${e.status} ${e.code}`) : String(e));
    } finally {
      setBusy(false);
    }
  }

  const LEVELS: { v: AccessLevel | ''; label: string }[] = [
    { v: '', label: '-' },
    { v: 'read', label: 'Čtení' },
    { v: 'write', label: 'Editace' },
  ];

  return (
    <>
      {stepUp && (
        <MfaStepUpModal
          title="Reset hesla - ověření"
          onClose={() => setStepUp(false)}
          onVerified={async () => { setStepUp(false); await submit(); }}
        />
      )}
      <div
        className="modal fade show settings-user-modal-layer"
        style={{ display: 'block' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-user-modal-title"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable settings-user-modal-dialog">
          <div className="modal-content settings-user-modal">
            <div className="modal-header">
              <div>
                <span className="settings-modal-eyebrow">Správa přístupů</span>
                <h5 className="modal-title" id="settings-user-modal-title">{isEdit ? 'Upravit uživatele' : 'Nový uživatel'}</h5>
                <p>{isEdit ? editUser?.email : 'Založte účet a nastavte přístup do Spaces.'}</p>
              </div>
              <button type="button" className="btn-close" aria-label="Zavřít" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {tempPassword ? (
                <div className="alert alert-success">
                  <p className="mb-1">
                    {isEdit ? 'Uloženo. Nové heslo' : 'Uživatel založen. Heslo'} (předej ručně, už se nezobrazí):
                  </p>
                  <code className="user-select-all fs-6">{tempPassword}</code>
                </div>
              ) : (
                <form id="new-user-form" onSubmit={onSubmit} autoComplete="off">
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">E-mail</label>
                      <input type="email" className="form-control" value={email} readOnly={isEdit}
                        onChange={(e) => setEmail(e.target.value)} required />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Jméno</label>
                      <input className="form-control" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Příjmení</label>
                      <input className="form-control" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Mattermost - e-mail nebo User ID <span className="text-muted fs-8">(pro notifikace)</span>
                    </label>
                    <div className="d-flex gap-2">
                      <input className="form-control" placeholder="napr. jmeno@firma.cz" value={mm}
                        onChange={(e) => { setMm(e.target.value); setMmCheck(null); }} />
                      <button type="button" className="btn btn-outline-secondary" style={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        disabled={mmChecking || !mm.trim()} onClick={() => void checkMm()}>
                        {mmChecking ? 'Ověřuji…' : 'Ověřit'}
                      </button>
                    </div>
                    {mmCheck && (
                      <div className={`fs-8 mt-1 ${mmCheck.ok ? 'text-success' : 'text-danger'}`}>
                        {mmCheck.ok
                          ? `✓ Napojení OK - ${mmCheck.displayName ?? mmCheck.username ?? mmCheck.userId} (${mmCheck.server})`
                          : `✗ ${mmCheck.error}`}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <label className="form-label mb-0">
                        {isEdit ? 'Nové heslo' : 'Heslo'}{' '}
                        <span className="text-muted fs-8">
                          {isEdit
                            ? '(nech prázdné = neměnit)'
                            : '(min. 12 znaků; nech prázdné = vygeneruje se jednorázové)'}
                        </span>
                      </label>
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        style={{ textTransform: 'none' }} onClick={doGenerate}>
                        Generovat heslo
                      </button>
                    </div>
                    <div className="input-group mt-1">
                      <input type={showPwd ? 'text' : 'password'} className="form-control" autoComplete="new-password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setGenerated(null); }}
                        placeholder={isEdit ? 'nech prázdné = neměnit' : '••••••••'} />
                      <button type="button" className="btn btn-outline-secondary" title="Zobrazit/skrýt"
                        onClick={() => setShowPwd((v) => !v)}>
                        {showPwd ? 'Skrýt' : 'Zobrazit'}
                      </button>
                    </div>
                    {generated && (
                      <div className="alert alert-info py-2 mt-2 mb-0">
                        Vygenerované heslo: <code style={{ fontSize: '1rem' }}>{generated}</code>{' '}
                        <button type="button" className="btn btn-sm btn-outline-secondary ms-1"
                          style={{ textTransform: 'none' }}
                          onClick={() => void navigator.clipboard?.writeText(generated)}>
                          Kopírovat
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-5">
                      <label className="form-label">Role</label>
                      <select className="form-select" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
                        <option value="user">Uživatel (práva per modul)</option>
                        <option value="admin">Admin projektu (plný přístup k vybraným Spaces)</option>
                        {callerSuper && <option value="super_admin">Super Admin (celé P1)</option>}
                      </select>
                    </div>
                  </div>

                  {role === 'super_admin' && (
                    <div className="alert alert-warning py-2">
                      Super Admin má plný přístup do <strong>celé P1</strong> (všechny Spaces + správa uživatelů).
                    </div>
                  )}

                  {role === 'admin' && (
                    <div className="mb-2">
                      <label className="form-label">Přístup k projektům (Spaces)</label>
                      <div className="border rounded p-2">
                        {catalog.length === 0 ? (
                          <span className="text-muted">žádné Spaces</span>
                        ) : (
                          catalog.map((sp) => (
                            <div className="form-check" key={sp.spaceId}>
                              <input className="form-check-input" type="checkbox" id={`adm-sp-${sp.spaceId}`}
                                checked={adminSpaceIds.includes(sp.spaceId)}
                                onChange={() => setAdminSpaceIds((p) => p.includes(sp.spaceId) ? p.filter((x) => x !== sp.spaceId) : [...p, sp.spaceId])} />
                              <label className="form-check-label" htmlFor={`adm-sp-${sp.spaceId}`}>
                                {sp.name} <span className="text-muted fs-8">({sp.slug})</span>
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {role === 'user' && (
                  <>
                  <label className="form-label">Přístup k modulům - čtení / editace per Space</label>
                  {catalog.length === 0 ? (
                    <p className="text-muted">Načítám katalog…</p>
                  ) : (
                    catalog.map((sp) => (
                      <div key={sp.spaceId} className="border rounded p-2 mb-2">
                        <div className="fw-semibold mb-2">{sp.name} <span className="text-muted fs-8">({sp.slug})</span></div>
                        {sp.modules.map((m) => {
                          const cur = levels[sp.spaceId]?.[m.key] ?? '';
                          return (
                            <div key={m.key} className="d-flex align-items-center justify-content-between py-1">
                              <span className="small">{m.label}</span>
                              <div className="btn-group btn-group-sm" role="group">
                                {LEVELS.map((opt) => (
                                  <button key={opt.label} type="button"
                                    className={`btn ${cur === opt.v ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    style={{ textTransform: 'none' }}
                                    onClick={() => setLevel(sp.spaceId, m.key, opt.v)}>
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                  </>
                  )}
                  {error && <div className="alert alert-danger mt-2">{error}</div>}
                </form>
              )}
              {!tempPassword && isEdit && editUser && callerSuper && <UserApiAccessSection userId={Number(editUser.id)} />}
            </div>
            <div className="modal-footer">
              {tempPassword ? (
                <button className="btn btn-primary" onClick={onDone}>Hotovo</button>
              ) : (
                <>
                  <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Zrušit</button>
                  <button type="submit" form="new-user-form" className="btn btn-primary" style={{ textTransform: 'none' }} disabled={busy}>
                    {busy ? 'Ukládám…' : isEdit ? 'Uložit' : 'Vytvořit + přiřadit'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show settings-user-modal-backdrop"></div>
    </>
  );
}

function MfaPane() {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { logout } = useAuth();

  useEffect(() => {
    mfaStatus().then(setStatus).catch((e) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo.'));
  }, []);

  async function regenerate() {
    setError(null);
    setBusy(true);
    try {
      const r = await mfaRegenerateBackupCodes(code.trim());
      setCodes(r.backupCodes);
      setCode('');
      setStatus((s) => (s ? { ...s, backupCodesLeft: r.backupCodes.length } : s));
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'MFA_INVALID_CODE' ? 'Kód není platný.' : 'Obnovení selhalo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-form-layout settings-password-pane">
      <section className="settings-form-panel">
        <header className="settings-panel-head">
          <div>
            <span>Zabezpečení</span>
            <h2>Dvoufázové ověření</h2>
            <p>Druhý faktor z ověřovací aplikace chrání účet i při úniku hesla.</p>
          </div>
        </header>
        <div className="settings-form-body">
          {!status ? (
            <p className="settings-loading">Načítám…</p>
          ) : status.enabled ? (
            <>
              <div className="alert alert-success">Dvoufázové ověření je zapnuté. Zbývá {status.backupCodesLeft} záložních kódů.</div>
              <div className="settings-field">
                <label className="form-label">Nové záložní kódy</label>
                <input
                  className="form-control"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="kód z aplikace"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <small>Staré kódy přestanou platit. Potvrďte aktuálním kódem z aplikace.</small>
              </div>
              {codes && (
                <div className="alert alert-info">
                  <strong>Uložte si nové záložní kódy, už se nezobrazí:</strong>
                  <ul className="mb-0 mt-2" style={{ columns: 2, fontFamily: 'monospace' }}>
                    {codes.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                </div>
              )}
              {error && <div className="alert alert-danger">{error}</div>}
            </>
          ) : (
            <>
              <div className="alert alert-warning">
                {status.enforced
                  ? 'Dvoufázové ověření je povinné. Nastavíte ho při příštím přihlášení.'
                  : 'Dvoufázové ověření zatím není zapnuté. Zapnete ho odhlášením a při dalším přihlášení.'}
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
            </>
          )}
        </div>
        <footer className="settings-form-footer">
          {status?.enabled ? (
            <button type="button" className="btn btn-primary" disabled={busy || !code.trim()} onClick={() => void regenerate()}>
              {busy ? 'Generuji…' : 'Vygenerovat záložní kódy'}
            </button>
          ) : (
            <button type="button" className="btn btn-outline-secondary" onClick={() => void logout()}>Odhlásit se</button>
          )}
        </footer>
      </section>
      <aside className="settings-context-panel">
        <span>Jak to funguje</span>
        <h2>Aplikace místo SMS</h2>
        <p>Kódy generuje ověřovací aplikace v telefonu (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden). Záložní kódy použijte, když telefon nemáte po ruce; každý platí jednou.</p>
      </aside>
    </div>
  );
}

interface AccountMailPreview {
  configured: boolean;
  from: { email: string | null; name: string | null };
  mails: { key: string; label: string; subject: string; html: string; text: string }[];
}

function AccountMailsPane() {
  const [data, setData] = useState<AccountMailPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get<AccountMailPreview>('/api/v1/auth/password-reset/preview')
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? `${e.status} ${e.code}` : String(e)));
  }, []);

  const mail = data?.mails[active];
  return (
    <section className="settings-form-panel" style={{ width: '100%' }}>
      <header className="settings-panel-head">
        <div>
          <span>Servisní e-maily účtu</span>
          <h2>Platform 1 - reset hesla a notifikace</h2>
          <p>
            {data
              ? data.configured
                ? `Odesílatel: ${data.from.name ?? ''} <${data.from.email ?? ''}> (SendGrid)`
                : 'Odesílání není nakonfigurováno (PLATFORM1_SENDGRID_API_KEY).'
              : 'Načítám…'}
          </p>
        </div>
      </header>
      <div className="settings-form-body">
        {error && <div className="alert alert-danger">{error}</div>}
        {data && (
          <>
            <ul className="nav nav-pills mb-3">
              {data.mails.map((m, i) => (
                <li className="nav-item" key={m.key}>
                  <button type="button" className={`nav-link${i === active ? ' active' : ''}`} onClick={() => setActive(i)}>{m.label}</button>
                </li>
              ))}
            </ul>
            {mail && (
              <>
                <div className="mb-2 small">Předmět: <strong>{mail.subject}</strong></div>
                <iframe title={mail.label} sandbox="allow-same-origin" srcDoc={mail.html} style={{ width: '100%', height: 520, border: '1px solid var(--bs-border-color)', borderRadius: 8, background: '#fff' }} />
                <details className="mt-2"><summary className="small">Textová verze</summary><pre className="small mt-2" style={{ whiteSpace: 'pre-wrap' }}>{mail.text}</pre></details>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
